import React, { useEffect, useState } from "react";
import {
    Drawer,
    Form,
    Input,
    Switch,
    Button,
    Space,
    Row,
    Col,
    message,
} from "antd";
import { CompassOutlined, EnvironmentOutlined } from "@ant-design/icons";
import {
    organizationAPIWithErrorHandling,
    organizationHelpers,
} from "../../../api/organization/organization";

const { TextArea } = Input;

// CRA chỉ nạp biến môi trường có tiền tố REACT_APP_, nên `process.env.GOOGLE_MAP_URL`
// trong .env không bao giờ tới được trình duyệt. Bản cũ đã có sẵn fallback về đúng
// địa chỉ này, để hằng số cho khỏi hiểu nhầm là đang cấu hình được.
const GOOGLE_MAPS_BASE = "https://www.google.com/maps";

/**
 * Drawer thêm mới / chỉnh sửa đơn vị.
 *
 * Màn chi tiết đơn vị vẫn là trang riêng (danh sách cán bộ, khách hàng, đợt thu,
 * thống kê), chỉ form nhập liệu chuyển vào drawer để giữ nguyên trang và thứ tự
 * cây đơn vị đang xem.
 *
 * Dữ liệu điền sẵn lấy thẳng từ dòng trên bảng: API danh sách trả model đầy đủ
 * nên không cần gọi lại getOrganizationById.
 *
 * @param {boolean} open
 * @param {object|null} record   Có -> chế độ sửa, null -> chế độ thêm mới.
 * @param {function} onClose
 * @param {function} onSaved     Gọi sau khi lưu thành công để trang danh sách nạp lại.
 */
export default function OrganizationFormDrawer({ open, record = null, onClose, onSaved }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const isEdit = !!record;

    useEffect(() => {
        if (!open) return;

        if (isEdit) {
            form.setFieldsValue({
                name: record.name,
                budget_code: record.budget_code,
                resident_count: record.resident_count,
                household_count: record.household_count,
                description: record.description,
                status: !!record.status,
            });
        } else {
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record]);

    // Hai nút bản đồ đọc thẳng ô "Chi tiết / Địa chỉ" đang nhập.
    const openMaps = (path) => {
        const address = (form.getFieldValue("description") || "").trim();
        if (!address) {
            message.warning("Vui lòng nhập địa chỉ trước!");
            return;
        }
        window.open(`${GOOGLE_MAPS_BASE}${path}${encodeURIComponent(address)}`, "_blank");
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const organizationData = organizationHelpers.formatOrganizationData(values);

            const validation = organizationHelpers.validateOrganizationData(organizationData);
            if (!validation.isValid) {
                Object.keys(validation.errors).forEach((field) => {
                    form.setFields([{ name: field, errors: [validation.errors[field]] }]);
                });
                return;
            }

            if (isEdit) {
                await organizationAPIWithErrorHandling.updateOrganization(record.id, organizationData);
                message.success("Cập nhật thông tin đơn vị thành công!");
            } else {
                await organizationAPIWithErrorHandling.createOrganization(organizationData);
                message.success("Thêm đơn vị thành công!");
            }

            onSaved?.();
            onClose?.();
        } catch (error) {
            // Drawer hẹp, toast dễ bị bỏ qua nên lỗi theo trường phải hiện ngay tại ô.
            if (error.fieldErrors) {
                form.setFields(
                    Object.entries(error.fieldErrors).map(([name, errors]) => ({
                        name,
                        errors: Array.isArray(errors) ? errors : [errors],
                    }))
                );
                message.error("Dữ liệu chưa hợp lệ, kiểm tra lại các ô được đánh dấu.");
            } else {
                message.error(
                    error.message || (isEdit ? "Không thể cập nhật đơn vị" : "Không thể thêm đơn vị")
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            title={isEdit ? "Chỉnh sửa đơn vị" : "Thêm đơn vị"}
            width="min(560px, 100vw)"
            open={open}
            onClose={onClose}
            destroyOnHidden
            maskClosable={!submitting}
            footer={
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={onClose} disabled={submitting}>
                        Hủy
                    </Button>
                    <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                        {isEdit ? "Lưu thay đổi" : "Thêm mới"}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark
                initialValues={{ status: true }}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={14}>
                        <Form.Item
                            name="name"
                            label="Tên đơn vị"
                            rules={[
                                { required: true, message: "Vui lòng nhập tên đơn vị!" },
                                { min: 2, message: "Tên đơn vị phải có ít nhất 2 ký tự!" },
                                { max: 100, message: "Tên đơn vị không được vượt quá 100 ký tự!" },
                            ]}
                        >
                            <Input placeholder="Nhập tên đơn vị" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={10}>
                        <Form.Item name="budget_code" label="Mã ngân sách">
                            <Input placeholder="Nhập mã ngân sách" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="resident_count" label="Số lượng cư dân">
                            <Input type="number" placeholder="Ví dụ: 1250" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="household_count" label="Số lượng hộ gia đình">
                            <Input type="number" placeholder="Ví dụ: 380" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="description" label="Chi tiết / Địa chỉ">
                    <TextArea
                        placeholder="Chi tiết đơn vị/phòng, địa chỉ..."
                        rows={4}
                        maxLength={500}
                        showCount
                    />
                </Form.Item>

                <Form.Item>
                    <Space wrap>
                        <Button
                            icon={<CompassOutlined />}
                            size="small"
                            onClick={() => openMaps("/dir/?api=1&destination=")}
                        >
                            Chỉ đường
                        </Button>
                        <Button
                            icon={<EnvironmentOutlined />}
                            size="small"
                            onClick={() => openMaps("/search/?api=1&query=")}
                        >
                            Mở Google Maps
                        </Button>
                    </Space>
                </Form.Item>

                <Form.Item name="status" label="Trạng thái" valuePropName="checked">
                    <Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
                </Form.Item>
            </Form>
        </Drawer>
    );
}
